import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Award, BookOpen, Target, CheckCircle, Tractor, Leaf } from 'lucide-react';

const MScAgricultureCoursePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/courses"
            className="inline-flex items-center text-green-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Courses
          </Link>
          <h1 className="text-4xl font-bold mb-4">M.Sc Agriculture - Master of Science in Agriculture</h1>
          <p className="text-xl text-green-100 max-w-3xl">
            An advanced 2-year postgraduate program that provides specialized knowledge and research skills in various agricultural disciplines for career advancement in agriculture sector.
          </p>
        </div>
      </div>

      {/* Course Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Overview</h2>
              <p className="text-gray-700 mb-4">
                Master of Science in Agriculture (M.Sc Agriculture) is a postgraduate degree program designed for graduates who want to specialize in specific areas of agriculture. The program offers advanced theoretical knowledge and practical research experience in various agricultural disciplines.
              </p>
              <p className="text-gray-700">
                Students gain expertise in advanced agricultural technologies, research methodologies, and sustainable farming practices, preparing them for leadership roles in agricultural research, education, and industry.
              </p>
            </div>

            {/* Course Structure */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Structure</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Duration & Structure</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      2 years (4 semesters)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Semester-based curriculum
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Research project/dissertation
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Specializations</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Agronomy</li>
                    <li>• Horticulture</li>
                    <li>• Agricultural Economics</li>
                    <li>• Soil Science</li>
                    <li>• Plant Breeding & Genetics</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Career Opportunities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Career Opportunities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Research & Academia</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Research Scientist</li>
                    <li>• Assistant Professor</li>
                    <li>• Agricultural Researcher</li>
                    <li>• Research Associate</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Industry & Government</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Agricultural Consultant</li>
                    <li>• Seed Company Executive</li>
                    <li>• Agricultural Officer</li>
                    <li>• Project Manager</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Criteria</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Academic Requirements</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• B.Sc Agriculture or related field</li>
                    <li>• Minimum 55-60% aggregate marks</li>
                    <li>• Some universities require entrance exams</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Prerequisites</h3>
                  <p className="text-gray-700">Strong foundation in agricultural sciences and research interest</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Duration: 2 Years</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Mode: Full-time</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Degree: M.Sc Agriculture</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">Research: Required</span>
                </div>
              </div>
            </div>

            {/* Top Universities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top Universities</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Tamil Nadu Agricultural University</h4>
                  <p className="text-sm text-gray-600">Coimbatore, Tamil Nadu</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Indian Agricultural Research Institute</h4>
                  <p className="text-sm text-gray-600">New Delhi</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Punjab Agricultural University</h4>
                  <p className="text-sm text-gray-600">Ludhiana, Punjab</p>
                </div>
              </div>
            </div>

            {/* Skills Developed */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Skills Developed</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm text-gray-700">Research Methodology</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm text-gray-700">Data Analysis</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm text-gray-700">Project Management</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm text-gray-700">Technical Writing</span>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-3">Need Guidance?</h3>
              <p className="mb-4">Get expert counseling for M.Sc Agriculture admissions</p>
              <Link
                to="/premium-services"
                className="inline-block bg-white text-green-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MScAgricultureCoursePage;